/* Unscoped styles */
import './assets/main.css'
import 'floating-vue/dist/style.css'

/* Vue */
import { createApp, nextTick } from 'vue'
import mitt from 'mitt'
import App from './App.vue'
import router from './router'

/* Resize obs */
import { VueWindowSizePlugin } from 'vue-window-size/plugin'

/* User auth */
import { jwtDecode } from 'jwt-decode'
import { getAuthToken, removeAuthToken } from './services/authStorage.js'

/* Error logging */
import * as Sentry from '@sentry/vue'

/* Used for tooltips */
import FloatingVue from 'floating-vue'

/* Google Analytics */
import VueGtag from 'vue-gtag'

const app = createApp(App)

/* Setup global statics */
const useLocalApi = import.meta.env.VITE_USE_LOCAL_API === 'true'
app.config.globalProperties.$api = useLocalApi ? 'http://localhost:8080/' : (import.meta.env.VITE_API_URL || 'https://auth.bamboonine.co.uk/')
app.config.globalProperties.$clients = [] /* Empty array to store the decoded JWT */
app.config.globalProperties.window = window /* Expose the global window object to Vue */

const emitter = mitt()
app.config.globalProperties.$bus = emitter /* Create a vue3 compatible global event bus */

/* Add domain to body attr */
const domain = (new URL(window.location.href)).origin
document.body.setAttribute('domain', domain)

/* Update title to indicate staging domain */
if (domain.endsWith('.dev')) document.title = `[STAGING] ${document.title}`

/* Error and performance logging via Sentry */
Sentry.init({
  enabled: process.env.NODE_ENV === 'production',
  app,
  release: import.meta.env.VITE_GIT_VER, /* Add in the git hash as the release version */
  dsn: 'https://043e1ae806fd186a6942ebad697831f4@o4504020857913344.ingest.us.sentry.io/4507095386947584',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration()
  ],
  tracesSampleRate: 1.0, /*  Capture 100% of the transactions for performance sampling */
  tracePropagationTargets: ['localhost', /^https:\/\/localghost\.com/, /^https:\/\/hub\.bamboonine\.co\.uk/],
  replaysSessionSampleRate: 0.1, /* 10% sample rate */
  replaysOnErrorSampleRate: 1.0 /* 100% sample rate */
})

app.use(FloatingVue) /* Add tool tip support */
app.use(VueWindowSizePlugin) /* Add window size plugin */
app.use(router) /* Add router */
app.use(VueGtag, { config: { id: 'G-DV1V408J65' } }) /* GA4 */
app.mount('#app')

/**
 * Checks if the access token exists in the local storage and is valid.
 * When valid, applies the decoded JWT claims to the app's global properties.
 * @returns {boolean} Returns true if a valid, non-expired JWT exists.
 */
const accessCheckTokenExists = () => {
  const token = getAuthToken()

  /* Check local storage for a JWT */
  if (!token) return false

  /* Decode and validate the JWT */
  const decoded = decodeAndValidateJwt(token)
  if (!decoded) return false

  /* Apply the decoded claims to app globals and Sentry */
  applyJwtClaims(decoded)
  return true
}

/**
 * Decode and validate a JWT token. Returns the decoded payload if valid,
 * or null if the token is expired, an unsupported version, or malformed.
 * Clears the stored JWT on failure to prevent stale auth state.
 *
 * @param {string} token - The JWT token to validate
 * @returns {Object|null} Decoded JWT payload, or null if invalid
 */
const decodeAndValidateJwt = (token) => {
  try {
    const decoded = jwtDecode(token)

    /* Check if the JWT is expired */
    if (decoded.exp < Date.now() / 1000) {
      removeAuthToken()
      return null
    }

    /* Reject outdated JWT versions so we can update token schema in the future */
    if (!decoded.ver || decoded.ver < 2) {
      removeAuthToken()
      return null
    }

    return decoded
  } catch (err) {
    /* Fails if the JWT is formed badly */
    removeAuthToken()
    return null
  }
}

/**
 * Apply decoded JWT claims to the app's global properties and Sentry.
 * This sets up the user context (email, admin access, client list) that
 * components read throughout the app.
 *
 * @param {Object} decoded - Decoded JWT payload from decodeAndValidateJwt
 */
const applyJwtClaims = (decoded) => {
  app.config.globalProperties.$clients = localStorage.getItem('clients') ? JSON.parse(localStorage.getItem('clients')) : []
  app.config.globalProperties.$email = decoded.email
  app.config.globalProperties.$sha = decoded.sha

  /* Update Sentry with the user email for error context */
  Sentry.setUser({ email: decoded.email })
  app.config.globalProperties.$admin = decoded.access
}

/* Before each route change, check if the user is logged in */
router.beforeEach((to, from, next) => {
  /**
   * Represents the access token check result.
   * @type {boolean}
  */
  const access = accessCheckTokenExists()

  /* Redirect to the login page if the user is not logged in */
  if (to.name !== 'login' && !access) {
    /* Update the attr element with the correct location */
    document.documentElement.setAttribute('route', 'login')
    /* Redirect the user */
    next({ name: 'login' })
    return
  }

  /* Redirect to the accounts page if the user is logged in and they attempt to access the login page */
  if (to.name === 'login' && access) {
    /* Update the attr element with the correct location */
    document.documentElement.setAttribute('route', 'accounts')
    /* Redirect the user */
    next({ name: 'accounts' })
    return
  }

  /* If we're trying to access an admin route and we're not an admin redirect to the no-entry page */
  if (to.path.startsWith('/admin/') && !app.config.globalProperties.$admin) {
    next({ name: 'restricted' })
    return
  }

  /* Add route name to the document element */
  document.documentElement.setAttribute('route', to.name)

  /* If we've visited the root URL and have been redirected to the acc. page */
  if ((to.name === 'accounts' && to.redirectedFrom?.path === '/')) {
    /* Check if the user has a single account */

    /* Decode their client data from the jwt */
    /* Get their client data from local storag */
    const jwt = app.config.globalProperties.$clients

    /* If the client only has access to a single dashboard take them to it directly, otherwise show them the accounts page */
    if (Object.values(jwt).length === 1) {
      next({ name: 'client', params: { id: Object.values(jwt)[0].id } })
    }
  }

  /* Continue request as expected without redirecting the user */
  next()
})

const DEFAULT_TITLE = 'Client Hub'
router.beforeEach((to) => {
  nextTick(() => {
    document.title = to.meta.title ? `${DEFAULT_TITLE} - ${to.meta.title}` : DEFAULT_TITLE
  })
})

/* Force a reload if the user encounters a dynamic import error due deployment caching */
router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
    window.location = to.fullPath
  }
})
